import { CynthaiLogo, ExcelIcon } from "@cynthai/icons";
import type { ButtonProps as RAButtonProps } from "react-aria-components";
import { Button as RAButton } from "react-aria-components";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import Spinner from "./Spinner";

const button = tv({
  base: `
    flex shrink-0 cursor-pointer items-center gap-3 rounded-md px-3 font-medium
    whitespace-nowrap text-text-primary transition-all duration-200
    disabled:cursor-not-allowed
    disabled:bg-tertiary/30 disabled:opacity-50
    data-[icon-position=right]:flex-row-reverse
    aria-expanded:[&_svg]:text-primary
    data-[no-expand-color=true]:aria-expanded:[&_svg]:text-current
  `,
  variants: {
    size: {
      lg: "h-12 min-w-10 px-5",
      md: "h-9 min-w-10 px-4 text-sm",
      sm: "h-8 min-w-10 px-3 text-sm",
      xs: "h-6 min-w-10 px-2 text-xs",
      square: "size-9 justify-center p-0",
      "square-md": "size-8 justify-center p-0",
      "square-sm": "size-7 justify-center p-0",
      "square-xs": "size-6 justify-center p-0",
      unsized: "h-auto",
      "square-lg": "size-12 justify-center p-0",
    },
    color: {
      default: "",
      primary: `
        bg-primary text-text-button
        hover:bg-primary/80
      `,
      secondary: `
        bg-secondary text-text-secondary
        hover:bg-secondary/80
      `,
      background: `
        bg-background-2
        hover:bg-background-2/80
      `,
      tertiary: `
        bg-tertiary
        hover:bg-tertiary/80
      `,
      danger: `
        bg-[#FF6653] text-[#FF6653]
        hover:bg-[#FF6653]/80
      `,
      success: `
        bg-[#98CF63] text-[#98CF63]
        hover:bg-[#98CF63]/80
      `,
      "success-ghost": `
        bg-[#98CF63]/10 text-[#98CF63]
        hover:bg-[#98CF63]/25
      `,
      "danger-ghost": `
        bg-[#FF6653]/10 text-[#FF6653]
        hover:bg-[#FF6653]/25
      `,
      "primary-ghost": `
        bg-primary/10 text-primary
        hover:bg-primary/20
      `,
    },
    variant: {
      default: "",
      ghost: `
        bg-transparent font-medium
        hover:bg-background
      `,
      bordered: "border border-border",
      "ask-cynthai":
        "border border-border bg-secondary gap-2.5 rounded-md font-normal",
      "export-excel": `
        gap-2.5 rounded-md border border-border bg-secondary font-normal
        text-text-primary hover:bg-secondary/80
      `,
      icon: `
        rounded-md border border-transparent p-1.75 text-text-secondary
        transition-all
        hover:border-border hover:bg-background/5 hover:text-text-primary
        data-[pressed=true]:border-transparent data-[pressed=true]:bg-primary/10
        data-[pressed=true]:text-primary
      `,
      "ghost-bordered": `
        border! border-border! bg-background/20! font-medium
        hover:bg-background
      `,
      "primary-ghost-bordered": `
        border! border-primary! bg-primary/10! font-medium text-primary
        hover:bg-primary/20
      `,
      "ghost-dashed": `
        rounded-md border! border-dashed! border-border! bg-border/10 px-2!
        font-medium
        hover:bg-background
      `,
      "danger-ghost": `
        bg-danger-400/15! font-medium text-danger-400
        hover:bg-danger-400 hover:text-white
      `,
      "success-ghost-bordered": `
        rounded-lg border! border-success! bg-success/5! font-medium
        text-success/80!
        hover:bg-success/10
      `,
      soft: `border! font-medium`,
      "ghost-light-bordered": `
        border! border-border/30! bg-background-2/50! font-medium
        hover:bg-background-2/70!
      `,
      inline: `
        inline rounded-none! bg-transparent! p-0! font-normal
        hover:bg-transparent!
      `,
      "toolbox-delete": `
        border border-danger/80 bg-danger font-medium
        hover:bg-danger/70
      `,
      "toolbox-edit": `
        border border-primary/80 bg-primary font-medium
        hover:bg-primary/70
      `,
      searchbar: `
        bg-secondary/80 px-3 py-2 text-text-secondary
        placeholder:text-text-tertiary
        focus-within:bg-background-2/80
        hover:bg-background-2/80
      `,
    },
    highlight: {
      true: "",
    },
    align: {
      start: "justify-start",
      center: "justify-center",
      end: "justify-end",
      between: "justify-between",
    },
  },
  defaultVariants: {
    size: "md",
    variant: "default",
    align: "center",
    color: "default",
  },
  compoundVariants: [
    {
      variant: "ghost",
      color: "primary",
      className: `
        text-text-button-ghost
        hover:bg-primary/10
      `,
    },
    {
      variant: "ghost",
      color: "danger",
      className: `
        text-danger
        hover:bg-danger/10
      `,
    },
    {
      variant: "ghost",
      color: "tertiary",
      className: "text-text-tertiary",
    },
    {
      variant: "ghost-light-bordered",
      color: "primary",
      className: `
        border! border-primary/30! bg-primary/10! font-medium text-primary/80
        hover:bg-primary/20!
      `,
    },
    {
      variant: "ghost-light-bordered",
      color: "secondary",
      className: `
        border! border-white/20! bg-secondary/80! font-medium text-white
        hover:bg-secondary/40!
      `,
    },
    {
      variant: "ghost-light-bordered",
      color: "tertiary",
      className: `
        border! border-tertiary/30! bg-tertiary/80! font-medium text-white
        hover:bg-tertiary/40!
      `,
    },
    {
      variant: "default",
      color: "danger",
      className: "text-text-button",
    },
    {
      variant: "default",
      highlight: true,
      className: `bg-tertiary text-primary`,
    },
    {
      variant: "soft",
      color: "danger",
      className: `
        border-danger-soft-dark/50! bg-danger-soft/10! text-danger-soft
        hover:bg-danger-soft/15!
      `,
    },
    {
      variant: "soft",
      color: "success",
      className: `
        border-success-soft-dark/50! bg-success-soft/10! text-success-soft
        hover:bg-success-soft/15!
      `,
    },
  ],
});

type IconPosition = "left" | "right";

export type ButtonBaseProps = Omit<
  RAButtonProps,
  "children" | "aria-label" | "isPending"
> &
  VariantProps<typeof button> & {
    highlight?: boolean;
    isChildrenVisible?: boolean;
    icon?: React.FunctionComponent<{
      width: number;
      height: number;
      size?: number;
      color: string;
      className?: string;
    }>;
    className?: string;
    isLoading?: boolean;
    iconSize?: number;
    iconPosition?: IconPosition;
    noExpandColor?: boolean;
  };

export type ButtonProps =
  | (ButtonBaseProps & { children: React.ReactNode; "aria-label"?: string })
  | (ButtonBaseProps & { children?: never; "aria-label": string });

const buttonVariantDefaults: Partial<
  Record<
    NonNullable<ButtonBaseProps["variant"]>,
    Pick<ButtonBaseProps, "icon" | "size" | "iconSize">
  >
> = {
  "ask-cynthai": { icon: CynthaiLogo, size: "sm", iconSize: 16 },
  "export-excel": { icon: ExcelIcon, size: "sm", iconSize: 16 },
};

type SpinnerColor = "white" | "primary" | "current";

function resolveSpinnerColor(
  color: ButtonBaseProps["color"],
  variant: ButtonBaseProps["variant"],
): SpinnerColor {
  if (
    color === "success" ||
    color === "success-ghost" ||
    variant === "toolbox-delete" ||
    variant === "toolbox-edit"
  )
    return "white";
  if (
    variant === "export-excel" ||
    color === "primary" ||
    color === "danger" ||
    color === "secondary" ||
    color === "tertiary" ||
    color === "background"
  )
    return "current";
  return "primary";
}

export default function Button({
  variant,
  icon: Icon = variant ? buttonVariantDefaults[variant]?.icon : undefined,
  isChildrenVisible = true,
  highlight = false,
  size = variant ? buttonVariantDefaults[variant]?.size : undefined,
  align,
  color,
  ref,
  isLoading,
  className,
  children,
  iconSize = (variant ? buttonVariantDefaults[variant]?.iconSize : undefined) ??
    20,
  iconPosition = "left",
  noExpandColor = false,
  ...rest
}: ButtonProps & {
  readonly ref?: React.RefObject<HTMLButtonElement | null>;
}) {
  return (
    <RAButton
      {...rest}
      data-highlight={highlight}
      data-icon-position={iconPosition}
      data-no-expand-color={noExpandColor || undefined}
      className={button({
        variant,
        size,
        highlight,
        align,
        className,
        color,
      })}
      isPending={isLoading}
      ref={ref}
    >
      {isLoading && (
        // decorative: the pending state is already conveyed by the button
        // itself, and the spinner's label must not leak into the button name
        <span aria-hidden="true" className="contents">
          <Spinner size="sm" color={resolveSpinnerColor(color, variant)} />
        </span>
      )}

      {!isLoading && Icon && (
        <Icon
          className="shrink-0"
          width={iconSize}
          size={iconSize}
          height={iconSize}
          color="currentColor"
        />
      )}

      {isChildrenVisible && children}
    </RAButton>
  );
}
